import React, { useMemo, useCallback } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import getLinkProps from '@/lib/getLinkProps';
import { changeRedirectUrl } from '@fh-components/fh-js-api-core/store/slices/userSlice';
import { isLoggedInSelector } from '@/store';
import { Title, Loader, SocialList, Button } from '@/components';
import { Container, BreadCrumbs } from '@/containers';
import { useSelector, useDispatch } from 'react-redux';
import { returnFormDocUrl, shopsIds } from './constants';
import { messengersList, messengersQualityList } from '@/lib/config';
import { Link as ScrollLink } from 'react-scroll';
import clsx from 'clsx';

import styles from './ReturnPolicyPage.module.scss';

const ShopsMap = dynamic(() => import('../../components/shopsMap/ShopsMap'), {
	loading: () => <Loader />,
});

const Content = () => {
	const dispatch = useDispatch();
	const loggedIn = useSelector(isLoggedInSelector);
	const [returnHref, returnAs] = getLinkProps({
		type: loggedIn ? 'new-return' : 'login',
	});

	const onLoginClick = useCallback(() => {
		const [href, asPath] = getLinkProps({ type: 'new-return' });

		dispatch(changeRedirectUrl({ href, asPath }));
	}, [dispatch]);

	return (
		<div>
			{/* Способы */}
			<div className={styles.registration}>
				<div className={styles.registrationInfo}>
					Оформить возврат товара вы можете на сайте, нажав кнопку ниже, либо{' '}
					<a
						className={styles.connection}
						href='https://media.fh.by/fh-public/docs/forma_vozvrata.docx'
					>
						скачать заявление здесь
					</a>{' '}
					и обратиться к администратору Интернет-магазина любым удобным способом:
					<div className={styles.socialList}>
						<SocialList list={messengersList} type={null} top />
					</div>
					<p className={styles.registrationPhone}>
						<a className={clsx(styles.text, styles.phone)} href='tel:+375296332633'>
							+375 (29) 633-2-633
						</a>
					</p>
				</div>
				<Link href={returnHref} as={returnAs}>
					<Button
						classNames={{ general: styles.backLink }}
						{...!loggedIn && { onClick: onLoginClick }}
						type='primary'
					>
						Оформить возврат
					</Button>
				</Link>
			</div>
			<div className={styles.textBlock} name='item6'>
				<Title component='h2'>Возврат товара осуществляется следующими способами</Title>
				<div className={styles.textContent}>
					<div className={styles.subTitleBlock}>
						<Title component='h3' className={styles.subTitle}>
							Самостоятельный возврат
						</Title>
						<p className={styles.text}>
							Вы можете вернуть товар по{' '}
							<span className={styles.selectedText}>
								предварительной договоренности с администратором
							</span>{' '}
							интернет-магазина в один из розничных магазинов сети (см. на{' '}
							<span className={styles.selectedText}>карте ниже</span>), расположенных в г. Минске.
						</p>
						<p className={styles.text}>
							Прием возвратов в магазинах осуществляется во второй половине дня{' '}
							<span className={styles.selectedText}>с 18:00 до 22:00</span>.
						</p>
						{/* Пункты возврата */}
						<div className={clsx(styles.textBlock, styles.returnPoint)} name='item7'>
							<Title component='h3' className={styles.subTitle}>
								Пункты возврата:
							</Title>
							<div className={styles.textContent}>
								<div className={styles.subTitleBlock}>
									<Title component='h3' className={styles.returnPointTitle}>
										ТЦ Green City Минск, ул. Притыцкого, 156
									</Title>
									<ul className={clsx(styles.text, styles.list)}>
										<li className={styles.listItem}>Women'Secret</li>
										<li className={styles.listItem}>Parfois</li>
										<li className={styles.listItem}>Jack&Jones</li>
									</ul>
								</div>
								<div className={styles.subTitleBlock}>
									<Title component='h3' className={styles.returnPointTitle}>
										ТЦ Замок Минск, пр. Победителей, 65
									</Title>
									<ul className={clsx(styles.text, styles.list)}>
										<li className={styles.listItem}>Gerry Weber</li>
										<li className={styles.listItem}>Weekend Max Mara</li>
										<li className={styles.listItem}>Women'Secret</li>
										<li className={styles.listItem}>Etam</li>
									</ul>
								</div>
								<div className={styles.subTitleBlock}>
									<Title component='h3' className={styles.returnPointTitle}>
										ТЦ Dana Mall Минск, ул. Петра Мстиславца, 11
									</Title>
									<ul className={clsx(styles.text, styles.list)}>
										<li className={styles.listItem}>Jack&Jones</li>
										<li className={styles.listItem}>Parfois</li>
										<li className={styles.listItem}>Gerry Weber</li>
										<li className={styles.listItem}>Etam</li>
										<li className={styles.listItem}>Women'Secret</li>
									</ul>
								</div>
							</div>
						</div>
					</div>
					<div className={styles.subTitleBlock}>
						<Title component='h3' className={styles.subTitle}>
							Возврат курьером по городу Минску
						</Title>
						<p className={styles.text}>
							Вы можете заказать курьера забрать Товар, подлежащий возврату. Данная услуга является
							бесплатной. Для заказа курьера необходимо связаться с администратором
							интернет-магазина.
						</p>
					</div>
					<div className={styles.subTitleBlock}>
						<Title component='h3' className={styles.subTitle}>
							Возврат через почтовые службы
						</Title>
						<p className={styles.text}>
							Вы можете{' '}
							<span className={styles.selectedText}>
								вернуть товар надлежащего качества в течение 14 дней после покупки (датой покупки
								считается дата доставки)
							</span>
							, а товар ненадлежащего качества можно вернуть в течение гарантийного срока.
						</p>
						<p className={styles.text}>
							Заполните заявление и отправьте его вместе с товаром удобным для вас способом — почта,
							любая курьерская служба.
						</p>
						<p className={styles.text}>
							Адрес возврата:{' '}
							<span className={styles.selectedText}>
								ОДО «БелВиринея» 220030, Республика Беларусь, г. Минск, ул. Немига, 5, пом. 39.
							</span>
						</p>
						<p className={styles.text}>
							Скачайте форму заявления{' '}
							<a
								className={styles.connection}
								href={returnFormDocUrl}
							>
								здесь
							</a>{' '}
							или свяжитесь с администратором для пересылки формы на удобный для вас мессенджер.
						</p>
						<p className={styles.text}>
							Связаться с администратором можно удобным для вас способом — телефон, Telegram, VIber,
							WhatsApp.
						</p>
						<p className={styles.text}>
							Тел:{' '}
							<a className={styles.connection} href='tel:+375296332633'>
								+375 (29) 633-2-633
							</a>
						</p>
						<p className={styles.text}>
							или по e-mail:{' '}
							<a className={styles.connection} href='mailto:fh@fh.by'>
								fh@fh.by
							</a>
						</p>
						<p className={styles.text}>
							При оплате онлайн возврат денежных средств будет осуществлен на карту, которую
							использовали для оплаты, сразу после получения товара.
						</p>
					</div>
				</div>
			</div>
			{/* Возврат денежных средств */}
			<div className={styles.textBlock} name='item8'>
				<Title component='h2'>Возврат денежных средств</Title>
				<div className={styles.textContent}>
					<p className={styles.text}>
						При возврате денежных средств (в случае возврата товара или возврата предоплаты) расчеты
						с Покупателем производятся в том же порядке, в котором производилась его оплата, если
						иное не установлено соглашением сторон.
					</p>
					<p className={clsx(styles.text, styles.selectedText, styles.articlePoint)}>
						Возврат денежных средств производится в следующем порядке:
					</p>

					<ul className={clsx(styles.text, styles.list)}>
						<li className={styles.listItem}>
							Если расчет производился наличными денежными средствами при приобретении товара в
							одном из пунктов самовывоза/ торговых объектов Продавца, то возврат производится
							наличными денежными средствами в том же пункте самовывоза/ торговом объекте.
						</li>
						<li className={styles.listItem}>
							Если расчет производился наличными денежными средствами при доставке товара курьером,
							то возврат производится наличными денежными средствами в магазинах ГК Fashion House в
							ТРЦ Dana Mall, ТЦ Замок, ТРЦ Green City{' '}
							<span className={styles.selectedText}>с 18:00 до 22:00</span>. Представитель
							Покупателя должен предъявить документ, подтверждающий полномочия на получение за
							Покупателя денежных средств, а также документ, удостоверяющий личность.
						</li>
						<li className={styles.listItem}>
							Если расчет производился банковской картой курьеру, то возврат производится в
							безналичном порядке на ту же банковскую карту, которой производилась оплата. Возможны
							иные варианты возврата денежных средств.
						</li>
						<li className={styles.listItem}>
							Если заказ был оплачен онлайн, возврат денежных средств будет осуществен на карту,
							которую использовали для оплаты, сразу после получения товара.
						</li>
						<li className={styles.listItem}>
							По соглашению сторон, если расчет производился наличными денежными средствами при
							доставке товара курьерской/почтовой службой за пределами города Минска, возврат
							денежных средств возможен на банковскую карту Покупателя после получения Продавцом
							возвращаемого товара. При этом возможно списание банком-эмитентом комиссии за
							зачисление денежных средств на карт-счет Покупателя. Расходы по уплате комиссии банку
							в таком случае несет Покупатель.
						</li>
					</ul>

					<p className={styles.text}>
						Перечисление денежных средств осуществляется Продавцом в срок не позднее семи
						календарных дней с момента возврата товара и получения письма от Покупателя, при условии
						указания в нем всей необходимой информации. Срок поступления денежных средств на карту
						Покупателя зависит от правил банка-эмитента и может составлять от 3 до 30 дней с момента
						осуществления возврата Продавцом.
					</p>
					<p className={styles.text}>
						Срок возврата денежных средств зависит от способа возврата и составляет не более 7 дней
						с даты поступления возвращенного товара на наш склад.
					</p>
				</div>
			</div>
			{/* Возврат и обмен товаров надлежащего качества */}
			<div className={styles.textBlock} name='item1'>
				<Title component='h2'>Возврат товара надлежащего качества</Title>
				<div className={styles.textContent}>
					<p className={styles.text}>
						Причины для возврата товара со стороны Покупателя могут быть следующие:
					</p>
					<ul className={clsx(styles.text, styles.list)}>
						<li className={styles.listItem}>Не подошел размер, фасон, цвет, длина, форма и т.п.</li>
						<li className={styles.listItem}>
							Оттенок полученного товара отличается от оттенка модели с фотографии, представленной
							на сайте интернет-магазина.
						</li>
					</ul>
					<p className={styles.text}>
						Возврат товара надлежащего качества производится при условии, что товар не был в
						употреблении, сохранены его потребительские свойства и имеются доказательства
						приобретения его у Продавца.
					</p>
					<p className={styles.text}>
						Покупатель вправе возвратить товар в момент осуществления доставки в присутствии курьера
						либо в течение 14 дней со следующего дня после покупки, получения.
					</p>
					<p className={styles.text}>
						При возврате товара надлежащего качества возврат уплаченной за товар денежной суммы
						подлежит удовлетворению в срок не позднее семи дней со дня возврата товара, при условии
						указания Покупателем всей необходимой для возврата денежных средств информации.
					</p>
					<p className={styles.text}>
						Перечень непродовольственных товаров надлежащего качества, не подлежащих возврату,
						утвержден Постановлением Совета Министров Республики Беларусь от 14 июня 2002 года №
						778.
					</p>
				</div>
			</div>
			{/* Перечень */}
			<div className={styles.textBlock} name='item2'>
				<Title component='h2'>
					Перечень непродовольственных товаров надлежащего качества, не подлежащих обмену и возврату
				</Title>
				<div className={styles.textContent}>
					<p className={styles.text}>
						1. Белье нательное, белье для новорожденных и детей ясельного возраста из всех видов
						тканей, корсетные изделия, бельевые трикотажные изделия (пижамы,сорочки, комбинации,
						корректирующее белье, боди), кроме спортивных.
					</p>
					<p className={styles.text}>2. Чулочно-носочные изделия.</p>
					<p className={styles.text}>3. Бижутерия.</p>
					<p className={styles.text}>
						4. Ручки всех видов, автоматические карандаши, стержни, маркеры, фломастеры и иные
						аналогичные товары.
					</p>
					<p className={styles.text}>
						5. Предметы личной гигиены (зубные щетки, расчески, бигуди для волос, губки, парики,
						шиньоны, лезвия для бритья и другие аналогичные товары).
					</p>
					<p className={styles.text}>6. Парфюмерно-косметические товары.</p>
					<p className={styles.text}>7. Часы.</p>
					<p className={styles.text}>
						8. Изделия из полимерных материалов, контактирующие с пищевыми продуктами, в том числе
						для разового использования (посуда и принадлежности столовые и кухонные, емкости и
						упаковочные материалы для хранения и транспортировки пищевых продуктов бытового
						назначения, в том числе для разового использования.
					</p>
				</div>
			</div>
			{/* Возврат товара ненадлежащего качества */}
			<div className={styles.textBlock}>
				<div className={styles.textSubBlock} name='item3'>
					<Title component='h2'>Возврат товара ненадлежащего качества</Title>
					<div className={styles.textContent}>
						<p className={styles.text}>
							Причиной для возврата товара со стороны Покупателя может быть производственный брак.
						</p>
						<p className={styles.text}>
							Претензии к качеству приобретенного товара, возникшие после получения и оплаты товара,
							рассматриваются в соответствии с законом о защите прав потребителей и гарантийными
							обязательствами Продавца. В связи с этим приобретение товара с доставкой не дает
							Покупателю право требования доставки приобретенного Товара в целях возврата и не дает
							возможности осуществлять возврат товара посредством выезда к покупателю.
						</p>
						<p className={styles.text}>
							Товар принимается Продавцом для проведения внутренней проверки качества. Проверка
							проводится в срок до 7 календарных дней.
						</p>
						<p className={styles.text}>
							Если в ходе проверки будет установлено, что дефект изделия возник до момента его
							передачи Покупателю или по причине, возникшей до его передачи, договор розничной
							купли-продажи подлежит расторжению, а Покупателю осуществляется возврат денежных
							средств в сроки, установленные законодательством Республики Беларусь.
						</p>
						<p className={styles.text}>
							Если в ходе проверки будет установлено, что дефект изделия возник после его передачи
							Покупателю или по причине, возникшей после передачи, возврат денежных средств,
							оплаченных Покупателем за товар не производится.
						</p>
						<p className={styles.text}>
							При возврате товара ненадлежащего качества возврат уплаченной за товар денежной суммы
							подлежит удовлетворению в срок не позднее семи дней со дня возврата товара, при
							условии указания Покупателем всей необходимой для возврата денежных средств
							информации.
						</p>
						<p className={styles.text}>
							В случае, если Покупатель воспользовался платной услугой по доставке возвращаемого
							Товара, и товар по результатам проверки качества товара признан товаром ненадлежащего
							качества, то расходы на услугу курьерской доставки будут возвращены потребителю в
							полном объёме вместе с денежными средствами, уплаченными за возвращаемый товар.
						</p>
					</div>
				</div>
				<div className={clsx(styles.textSubBlock, styles.registration)} name='item9'>
					<div className={styles.registrationInfo}>
						<p className={styles.text}>
							Если у вас возникли вопросы, касающиеся принятого решения в ходе внутренней проверки
							качества, вы можете связаться с менеджером по качеству с 9:00 до 18:00 в будние дни
							любым удобным для вас способом:
						</p>
						<div className={styles.socialList}>
							<SocialList list={messengersQualityList} type={null} top />
						</div>
						<p className={styles.registrationPhone}>
							<a className={clsx(styles.text, styles.phone)} href='tel:+375445849765'>
								+375 (44) 584-97-65
							</a>
						</p>
					</div>
				</div>
			</div>
			{/* Гарантийный срок */}
			<div className={styles.textBlock} name='item4'>
				<Title component='h2'>Гарантийный срок</Title>
				<div className={styles.textContent}>
					<p className={styles.text}>
						Гарантийный срок на одежду законодательством Республики Беларусь не установлен.
					</p>
					<p className={styles.text}>Гарантийный срок на другие товары составляет:</p>
					<p className={styles.text}>
						Обувь – 30 дней, для обуви сезонного назначения - с начала сезона, для всесезонной обуви
						– со дня приобретения товара.
					</p>
					<p className={styles.text}>
						Для обуви сезонного назначения гарантийные сроки носки исчисляются:
					</p>
					<ul className={clsx(styles.text, styles.list)}>
						<li className={styles.listItem}>для зимней - с 15 ноября,</li>
						<li className={styles.listItem}>весенней - с 1 марта,</li>
						<li className={styles.listItem}>осенней - с 15 сентября,</li>
						<li className={styles.listItem}>летней - с 1 мая.</li>
					</ul>
					<p className={styles.text}>Галантерейные товары:</p>
					<ul className={clsx(styles.text, styles.list)}>
						<li className={styles.listItem}>
							50 дней – для сумок женских, мужских, дорожных, спортивных;
						</li>
						<li className={styles.listItem}>
							30 дней – для сумок хозяйственных, пляжных, для учащихся, портфелей ученических,
							ранцев, изделий мелкой кожгалантереи;
						</li>
						<li className={styles.listItem}>
							70 дней - для чемоданов, портфелей деловых и дорожных, папок.
						</li>
						<li className={styles.listItem}>12 месяцев – для зонтов.</li>
					</ul>
					<p className={styles.text}>Часы наручные кварцевые электронные – 6 месяцев.</p>
				</div>
			</div>
		</div>
	);
};

const Navigation = ({ isLast }) => {
	return (
		<div className={styles.navigation}>
			<div className={clsx(styles.navigationBlock, isLast && styles.navigationBlockLast)}>
				<ul>
					<li className={styles.navigationItem}>
						<ScrollLink activeClass={styles.active} spy smooth to='item6' offset={-60}>
							Возврат товара осуществляется следующими способами
						</ScrollLink>
					</li>
					<li className={styles.navigationItem}>
						<ScrollLink activeClass={styles.active} spy smooth to='item8' offset={-60}>
							Возврат денежных средств
						</ScrollLink>
					</li>
					<li className={styles.navigationItem}>
						<ScrollLink activeClass={styles.active} spy smooth to='item1' offset={-60}>
							Возврата товара надлежащего качества
						</ScrollLink>
					</li>
					<li className={styles.navigationItem}>
						<ScrollLink activeClass={styles.active} spy smooth to='item2' offset={-60}>
							Перечень непродовольственных товаров надлежащего качества, не подлежащих возврату
						</ScrollLink>
					</li>
					<li className={styles.navigationItem}>
						<ScrollLink activeClass={styles.active} spy smooth to='item3' offset={-60}>
							Возврат товара ненадлежащего качества
						</ScrollLink>
					</li>
					<li className={styles.navigationItem}>
						<ScrollLink activeClass={styles.active} spy smooth to='item9' offset={-60}>
							Контакты менеджера по качеству
						</ScrollLink>
					</li>
					<li className={styles.navigationItem}>
						<ScrollLink activeClass={styles.active} spy smooth to='item4' offset={-60}>
							Гарантийный срок
						</ScrollLink>
					</li>
				</ul>
			</div>
		</div>
	);
};

const ReturnPolicyPage = ({ shops }) => {
	const breadcrumbs = [
		{
			slug: '/',
			name: 'Возврат товара',
		},
	];

	const shopsOnMap = useMemo(() => {
		return shops.filter(shop => shopsIds[shop.id]);
	}, [shops]);

	return (
		<section className={styles.policySection}>
			<Container>
				<div className={styles.breadCrumbs}>
					<BreadCrumbs breadcrumbs={breadcrumbs} />
				</div>

				<Title component='h1'>Возврат товара</Title>

				<div className={styles.policy}>
					<Content />
					<Navigation />
				</div>
				<Title component='h3' className={styles.info}>
					Если вас интересует дополнительная информация по возврату, свяжитесь с нами любым удобным
					для Вас способом: по телефону:{' '}
					<a className={styles.connection} href='tel:+375296332633'>
						+375 (29) 633-2-633
					</a>{' '}
					или отправив заявку по электронной почте{' '}
					<a className={styles.connection} href='mailto:fh@fh.by'>
						fh@fh.by
					</a>
					.
				</Title>
			</Container>
			{/* Карта */}
			<div>
				<ShopsMap shops={shopsOnMap} hiddenTabs />
			</div>
		</section>
	);
};

export default ReturnPolicyPage;
